import React, { useEffect, useState } from 'react'
import {
  CROSS_SELL_MODAL_CLOSED,
  CROSS_SELL_MODAL_IS_OPENED,
  PRODUCT_ADDED_BUS_EVENT_TYPE,
  PRODUCT_ADDING_BUS_EVENT_TYPE,
  PRODUCT_INFO_FRAGMENT_RENDERED
} from '../../pageBusEvents'
import {
  SHOW_ALTERNATIVES_ADD_TO_BASKET,
  SHOW_CROSS_SELL_MODAL,
  SHOW_CROSS_SELL_MODAL_L2_CATEGORY,
  SHOW_CROSS_SELL_MODAL_L3_CATEGORY,
  SHOW_SURVEY_PDP
} from 'common/constants'
import { getCookie, setCookie, chemistryTheme, propConverter, Text } from 'chemistry'
import { ChakraProvider } from '@chakra-ui/react'
import selector from './selectors/product'
import { dispatch, subscribeAndProcess } from 'common/pageBus'
import { getPriceProps } from '../../common/price'
import { getProductName } from '../../common/selectors/product'
import { TargetProductBanner } from 'client/fragments/productDetailsAddedToBasket/components/TargetProductBanner/TargetProductBanner'
import { PopupButtons } from 'client/fragments/productDetailsAddedToBasket/components/PopupButtons/PopupButtons'
import { SurveyBanner } from 'client/fragments/productDetailsAddedToBasket/components/SurveyBanner/SurveyBanner'
import { Dialog } from 'client/fragments/productDetailsAddedToBasket/components/Dialog/Dialog'
import { Card } from 'client/fragments/productDetailsAddedToBasket/components/Card/Card'
import { getTranslator, TranslateContextProvider } from 'client/common/translator'

const Component = props => {
  const { showSurvey, showAlternatives, runningABTestsFlags, translationsPdp } = props

  const [productProps, setProductProps] = useState({})
  const [wasVisible, setWasVisible] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [isAdded, setIsAdded] = useState(false)
  const [ccAddedTitle, setCcAddedTitle] = useState('')
  const [isSurveyPassed, setIsSurveyPassed] = useState(false)
  const [selected, setSelected] = useState(false)

  useEffect(() => {
    subscribeAndProcess('pdp-add-to-basket', PRODUCT_INFO_FRAGMENT_RENDERED, event => setProductProps(event.props))
    subscribeAndProcess('pdp-add-to-basket', PRODUCT_ADDING_BUS_EVENT_TYPE, event => {
      setCcAddedTitle(event?.ccAddedTitle ?? '')
      setWasVisible(true)
      setIsOpen(true)

      if (runningABTestsFlags && SHOW_SURVEY_PDP in runningABTestsFlags) {
        window?.dataLayer?.push({
          event: 'ldFlaggedFeature',
          ldFlag: SHOW_SURVEY_PDP,
          ldFlagStatus: true,
          ldVariation: runningABTestsFlags[SHOW_SURVEY_PDP]
        })
      }

      // should be removed after final decision about alternatives
      if (runningABTestsFlags && productProps.breadcrumbs && SHOW_ALTERNATIVES_ADD_TO_BASKET in runningABTestsFlags) {
        window?.dataLayer?.push({
          event: 'ldFlaggedFeature',
          ldFlag: SHOW_ALTERNATIVES_ADD_TO_BASKET,
          ldFlagStatus: true,
          ldVariation: runningABTestsFlags[SHOW_ALTERNATIVES_ADD_TO_BASKET]
        })
        dispatch({
          type: SHOW_CROSS_SELL_MODAL,
          data: {
            eventAction: SHOW_CROSS_SELL_MODAL_L2_CATEGORY,
            eventLabel: productProps.breadcrumbs[1]?.categoryCode
          }
        })
        dispatch({
          type: SHOW_CROSS_SELL_MODAL,
          data: {
            eventAction: SHOW_CROSS_SELL_MODAL_L3_CATEGORY,
            eventLabel: productProps.breadcrumbs[2]?.categoryCode
          }
        })
      }
    })
    subscribeAndProcess('pdp-add-to-basket', PRODUCT_ADDED_BUS_EVENT_TYPE, () => setIsAdded(true))
  }, [productProps.breadcrumbs, runningABTestsFlags])

  useEffect(() => {
    if (showSurvey && !isSurveyPassed) {
      setIsSurveyPassed(!!getCookie('survey-finished'))
    }
    if (!isOpen) {
      dispatch({
        type: CROSS_SELL_MODAL_CLOSED
      })
    } else {
      dispatch({
        type: CROSS_SELL_MODAL_IS_OPENED,
        productCode: productProps?.productId
      })
    }
  }, [isOpen, isSurveyPassed, productProps?.productId, showSurvey])

  if (!wasVisible) return null

  const { lang, format, productDetails, isGuest, productUrl, showPraxisPlusHint } = productProps

  if (!productDetails) return <div />

  const product = selector(productDetails, isGuest, null)
  const t = getTranslator(translationsPdp)
  const priceData = getPriceProps(productDetails, lang, format, isGuest, productUrl, showPraxisPlusHint, t)?.priceProps

  const name = getProductName(productDetails)

  const { showPriceLabel, labelText, percentage, ...priceProps } = propConverter(priceData)
  priceProps.units = null // we always show package price

  const finishSurvey = () => {
    if (!showSurvey || isSurveyPassed) return
    if (selected) {
      window?.dataLayer?.push({
        event: 'ldFlaggedFeature',
        ldFlag: 'submit_survey_rating',
        ldFlagStatus: true,
        ldVariation: selected
      })
    } else {
      window?.dataLayer?.push({
        event: 'ldFlaggedFeature',
        ldFlag: 'close_survey',
        ldFlagStatus: true,
        ldVariation: ''
      })
    }
    setCookie('survey-finished', true, { path: '/' })
  }

  return (
    <ChakraProvider resetCSS={false} theme={chemistryTheme}>
      <TranslateContextProvider translations={translationsPdp}>
        <Dialog setIsOpen={setIsOpen} isOpen={isOpen} finishSurvey={finishSurvey}>
          <div id='productDetailsAddedToBasketContainer' className='flex flex-column col-12'>
            {showSurvey && !isSurveyPassed && (
              <SurveyBanner locale={props.lang} setSelected={setSelected} selected={selected} />
            )}
            <Text fontSize='3xl' fontFamily='bold' mb={3}>
              {t('product_added_to_basket_title')} {ccAddedTitle}
            </Text>
            <div className='flex flex-wrap justify-between'>
              <Card {...{ product, name, showPriceLabel, labelText, percentage, priceProps }} />
              <PopupButtons isAdded={isAdded} lang={props.lang} setIsOpen={setIsOpen} finishSurvey={finishSurvey} />
            </div>
            <TargetProductBanner {...productProps} />
            {showAlternatives && <div id='crossSellContainer'></div>}
          </div>
        </Dialog>
      </TranslateContextProvider>
    </ChakraProvider>
  )
}

export default { Component }
